/**
 * Series - 블로그 시리즈 전용 페이지 데이터
 *
 * CMS 의 series 카테고리 포스트 1개를 시리즈 하나의 설정 소스로 사용한다.
 * 시리즈 식별자는 그 포스트의 WP 슬러그(post_name)이고, 본문에는 시리즈 표시명·카드
 * 설명문·썸네일·연관 시리즈가 `key: value` 라인으로 들어간다
 * (ko-label / en-label / ko-description / en-description / image / related-series).
 * 멤버 포스트는 각 블로그 포스트의 ACF `blog.series`(= 시리즈 slug)로 연결되며,
 * 이미 캐시된 블로그 리스트(S3)에서 `series === {slug}` 인 포스트를 필터해 만든다
 * (라이브 조회 없음 — 리스트 캐시에 series 필드가 포함되어 있어야 함).
 */

import { CONTENT_CATEGORY_ID } from '@/constants/content-categoryId';
import { toEpoch } from '@/utils/date';
import { toPostSummary, type PostSummary } from '@/utils/post-summary';
import { CMS_IMAGE_MARKER_LINE, toCmsSourceLines } from '@/utils/sanitize';
import { fetchFromCache, FetchAPIOptions } from './api';
import {
  getBlogNewsPostsForList,
  type BlogNewsListItemProp,
} from './blog-news';
import { fetchFromWordpress } from './wordpress-api';
import { uploadWordpressCacheDataToS3 } from './wordpress-cache';

// ============================================
// Types
// ============================================

/** 시리즈 멤버 포스트 요약 — 페이지에서 locale 로 필터하기 위해 lang 포함 */
export interface SeriesPost extends PostSummary {
  lang: string[];
}

/** 목록 캐시 빌드가 WordPress 에서 받는 정의 포스트 (카드에 필요한 것만) */
interface SeriesDefinitionPost {
  slug: string;
  content: string;
  date: string; // WP 발행일(post_date) — ACF `date` 필드가 아니다
}

/** 소스 포스트(시리즈 설정 글) 본문 파싱 결과 */
export interface SeriesSource {
  label: { ko: string; en: string }; // 시리즈 표시명 (ko-label / en-label)
  description: { ko: string; en: string }; // 카드 설명문 (ko-description / en-description)
  image: string; // 카드 썸네일 (image) — 로케일 공용
  relatedSeries: string[]; // 연관 시리즈 slug (related series)
}

/** 시리즈 목록 카드의 로케일별 값 */
export interface SeriesListLocaleData {
  name: string; // 시리즈 표시명 (CMS 미입력이면 빈 문자열 — 폴백은 소비 측 정책)
  description: string; // 카드 설명문
  postCount: number; // 이 로케일의 멤버 글 수
}

/**
 * 시리즈 목록 캐시의 원소 — 시리즈 카드가 쓰는 것만 담는다.
 *
 * 로케일별 값을 한 원소에 같이 담아 로케일마다 캐시를 나누지 않는다(tag·editors-pick 캐시와
 * 같은 패턴). 소비 측은 `item[locale]` 하나만 꺼내 쓴다.
 */
export interface SeriesListItemData {
  slug: string;
  image: string; // 카드 썸네일 — 로케일 공용
  ko: SeriesListLocaleData;
  en: SeriesListLocaleData;
}

/** 시리즈별 캐시 데이터 */
export interface SeriesPageData {
  slug: string;
  label: { ko: string; en: string }; // 시리즈 표시명 — 페이지에서 locale 에 맞게 사용
  // 시리즈 카드(목록·랜딩)용 설명문·썸네일. 시리즈 상세 페이지는 쓰지 않는다.
  // 입력되지 않았으면 빈 문자열 — 사용처가 설명 줄을 생략하거나 기본 이미지로 떨어진다.
  description: { ko: string; en: string };
  image: string;
  relatedSeries: string[]; // 연관 시리즈 slug
  posts: SeriesPost[]; // date 오름차순(오래된 순 = 에피소드 순), 동률이면 databaseId 오름차순
  totalCount: number; // posts.length (전 locale 멤버)
}

// ============================================
// 상수
// ============================================

/** 캐시 폴더명이자 revalidateTag 태그 — content-revalidation.ts 의 series 태그와 일치해야 함 */
export const getSeriesCacheLabel = (slug: string) => `getSeries-${slug}`;

/**
 * 시리즈 목록 캐시 라벨이자 revalidateTag 태그.
 *
 * ⚠️ 태그의 `getTagList` 와 달리 이 캐시는 **렌더에 쓰인다**(시리즈 목록 페이지 + 블로그 랜딩
 * 시리즈 섹션). 캐시를 읽으면 `fetchWordpressCacheData` 가 이 라벨을 Next 태그로 바인딩하므로,
 * 재생성할 때 무효화도 함께 해야 두 화면이 stale 되지 않는다 — 그 짝을 놓치지 않도록
 * `syncSeriesList`(content-cache-handlers) 한 곳에서만 재생성한다.
 */
export const SERIES_LIST_CACHE_LABEL = 'getSeriesList';

/**
 * 목록 페이지 URL(`/blog/series/all/{page}`)이 선점한 세그먼트라 시리즈 slug 로 쓸 수 없다.
 * 정적 세그먼트가 `[slug]` 를 이기므로, 이 slug 의 시리즈가 생기면 상세 페이지가 영구
 * 접근 불가가 된다(404 도 에러도 없이 목록이 렌더된다 — 조용한 실패).
 */
export const RESERVED_SERIES_SLUG = 'all';

// 소스 포스트 조회 — series slug(= post_name)로 본문(연관 시리즈) 조회.
// prod_deployed 필터가 먹도록 단건(post) 이 아니라 connection(posts) 형태로 조회한다.
const SERIES_SOURCE_QUERY = `
query getSeriesSource($slugs: [String]) {
  posts(first: 1, where: { nameIn: $slugs, categoryName: "series", status: PUBLISH }) {
    nodes {
      content
    }
  }
}`;

// 목록 캐시용 전체 정의 포스트 조회 — 카드에 필요한 것만 받는다(slug·본문·발행일).
// 범용 getItems 를 쓰지 않는 이유:
//  - getItems 는 categoryId(term_id) 로 필터한다. term_id 는 dev 17 / 운영 165 로 갈리므로
//    slug 기반 categoryName 이 환경 중립이다(SERIES_SOURCE_QUERY 와 같은 방식).
//  - getItems(refresh) 는 fetchItems-series 를 S3 에 재업로드하는 부수효과가 있다. 목록 집계가
//    유니버스 캐시를 덮어쓸 이유가 없다.
//  - getItems 의 `date` 는 ACF 커스텀 필드(meta.date)이고 시리즈 정의글은 전건 미입력이다.
//    정렬에 필요한 건 WP 발행일(node.date)이라 그것만 직접 받는다.
const SERIES_LIST_QUERY = `
query getSeriesList($postCount: Int!) {
  posts(first: $postCount, where: { categoryName: "series", status: PUBLISH }) {
    pageInfo {
      hasNextPage
    }
    nodes {
      slug
      content
      date
    }
  }
}`;

/** 한 번에 받아오는 시리즈 정의글 수 — 현재 5건이라 여유가 크다(초과 시 중단) */
const SERIES_LIST_PAGE_SIZE = 100;

// ============================================
// 본문 파싱
// ============================================

/**
 * 소스 포스트 본문(HTML)에서 시리즈 표시명·카드 설명문·썸네일·연관 시리즈를 추출한다.
 *
 * 본문은 라인 단위 `key: value` 형식이다(키에 공백·콜론 앞뒤 공백 허용):
 *   ko-label: 월간 재분석 리포트
 *   en-label: Monthly Reanalysis Report
 *   ko-description: 카드에 쓸 설명
 *   en-description: card description
 *   image:                              ← 값이 비어 있으면 바로 다음 본문 이미지를 쓴다
 *   related-series : beyond-the-variant, gebra-use-guide
 *
 * `image:` 의 "빈 값 → 다음 이미지" 규칙은 editors-pick 소스와 같은 관례이고, 그 마커를
 * 만드는 정규화는 `toCmsSourceLines` 가 공유한다.
 */
export const parseSeriesSource = (content: string): SeriesSource => {
  const lines = toCmsSourceLines(content);

  const label = { ko: '', en: '' };
  const description = { ko: '', en: '' };
  let image = '';
  let relatedSeries: string[] = [];

  // 값이 빈 `image:` 라인이 다음 본문 이미지를 예약한 상태
  let imagePending = false;

  for (const line of lines) {
    // 마커 라인은 `http://` 때문에 콜론을 품고 있어 아래 key/value 파싱보다 먼저 걸러낸다
    const imgMatch = line.match(CMS_IMAGE_MARKER_LINE);
    if (imgMatch) {
      if (imagePending && imgMatch[1]) {
        image = imgMatch[1];
        imagePending = false;
      }
      continue;
    }

    const colon = line.indexOf(':');
    if (colon === -1) continue;
    const key = line.slice(0, colon).trim().toLowerCase();
    const value = line.slice(colon + 1).trim();

    // 라벨·설명문에 섞인 WP 엔티티(`&#038;` 등)는 `toCmsSourceLines` 가 이미 풀어 준다
    if (key === 'ko-label') {
      label.ko = value;
    } else if (key === 'en-label') {
      label.en = value;
    } else if (key === 'ko-description') {
      description.ko = value;
    } else if (key === 'en-description') {
      description.en = value;
    } else if (key === 'image') {
      if (value) {
        image = value;
      } else {
        imagePending = true;
      }
    } else if (key === 'related-series') {
      relatedSeries = value
        .split(',')
        .map(slug => slug.trim())
        .filter(Boolean);
    }
  }

  return { label, description, image, relatedSeries };
};

// ============================================
// 캐시 데이터 빌드
// ============================================

type BuildOptions = Pick<FetchAPIOptions, 'prodFilter'>;

/**
 * 소스 포스트 조회 + 본문 파싱 → 시리즈 표시명 + 연관 시리즈 slug
 *
 * 시리즈 설정 포스트는 자체 운영 배포 버튼을 가지는 prod_deployed 대상이다
 * (저장 → 개발 반영, 운영 배포 버튼 → 운영 반영). 따라서 다른 공개 콘텐츠처럼
 * prodFilter 를 적용한다 — 운영 refresh 시 미배포 설정 포스트는 fail-fast 로 실패한다.
 */
const fetchSeriesSource = async (
  slug: string,
  options?: BuildOptions,
): Promise<SeriesSource> => {
  const sourceData = await fetchFromWordpress(
    SERIES_SOURCE_QUERY,
    { variables: { slugs: [slug] } },
    options,
  );
  const content: string | undefined = sourceData?.posts?.nodes?.[0]?.content;
  if (content === undefined || content === null) {
    throw new Error(`series 소스 포스트를 찾을 수 없습니다: ${slug}`);
  }

  return parseSeriesSource(content);
};

/** 블로그 리스트 아이템 → 시리즈 멤버 포스트 요약 (공용 요약 + 페이지 필터용 lang) */
const listItemToSeriesPost = (item: BlogNewsListItemProp): SeriesPost => ({
  ...toPostSummary(item),
  lang: item.lang ?? [],
});

/**
 * 소스(표시명·연관 시리즈) 조회 + 캐시된 블로그 리스트에서 멤버 필터 → SeriesPageData.
 *
 * 멤버는 라이브 조회 없이 이미 S3 에 캐시된 블로그 리스트(`getBlogNewsPostsForList`)에서
 * `series === slug` 로 걸러낸다. 시리즈는 이 리스트를 **소비만** 한다 — 여기서 블로그 리스트를
 * 다시 만들지 않는다(부수효과 금지). 따라서 정확한 멤버를 얻으려면 블로그 리스트 캐시가
 * 최신이어야 한다: 블로그 저장 웹훅이 리스트를 갱신한 뒤 커플링이 돌고, `all` init 은
 * blog 갱신 뒤 시리즈를 돌린다. `series` init 단독 실행은 기존 blog 캐시를 그대로 쓴다.
 * (S3 업로드 없음 — 검증/dry-run 용으로도 사용)
 */
export const buildSeriesData = async (
  slug: string,
  options?: BuildOptions,
): Promise<SeriesPageData> => {
  const [source, allPosts] = await Promise.all([
    fetchSeriesSource(slug, options),
    getBlogNewsPostsForList(CONTENT_CATEGORY_ID.blog),
  ]);

  const posts = allPosts
    .filter(post => post.series === slug)
    // date 오름차순(오래된 순 = 에피소드 순) — 저장 시점에 확정.
    // 같은 날 발행된 시리즈물이 실재해(날짜만으로는 동률) databaseId(= WP 글 생성 순)
    // 오름차순을 2차 키로 둔다. 2차 키가 없으면 안정 정렬이 입력(블로그 리스트 =
    // 최신순) 순서를 그대로 남겨 회차가 거꾸로 보인다.
    .sort(
      (a, b) =>
        toEpoch(a.date) - toEpoch(b.date) || a.databaseId - b.databaseId,
    )
    .map(listItemToSeriesPost);

  return {
    slug,
    label: source.label,
    description: source.description,
    image: source.image,
    relatedSeries: source.relatedSeries,
    posts,
    totalCount: posts.length,
  };
};

// ============================================
// 시리즈 목록 캐시 (전체 시리즈의 카드 데이터)
// ============================================

/** 목록 조회 응답의 shape — fetchFromWordpress 는 타입 없는 JSON 을 준다 */
interface SeriesListResponse {
  posts?: {
    pageInfo?: { hasNextPage?: boolean };
    nodes?: unknown[];
  };
}

/**
 * 목록 조회 응답 → 검증된 정의 포스트.
 *
 * 잘린 응답과 빈 응답은 여기서 끊는다 — 그대로 통과시키면 틀린 목록을 캐시에 박제해
 * 목록 페이지와 랜딩이 함께 어긋난다(최신순 100건만 받으므로 잘리면 오래된 시리즈가 사라진다).
 */
const toSeriesDefinitionPosts = (
  sourceData: SeriesListResponse | null | undefined,
): SeriesDefinitionPost[] => {
  const rawNodes: unknown[] = sourceData?.posts?.nodes ?? [];
  const items = rawNodes.filter(isSeriesDefinitionPost);

  // 스키마가 어긋난 노드는 걸러졌다 — 조용히 사라지면 원인을 못 찾는다
  if (items.length !== rawNodes.length) {
    console.warn(
      `📚 시리즈 정의글 ${rawNodes.length - items.length}건이 스키마 불일치로 제외됐습니다(본문 없이 발행된 글일 수 있습니다)`,
    );
  }

  if (sourceData?.posts?.pageInfo?.hasNextPage) {
    throw new Error(
      `시리즈 정의글이 ${SERIES_LIST_PAGE_SIZE}건을 넘습니다 — 잘린 목록을 캐시에 쓰지 않고 중단합니다. 페이지네이션이 필요합니다`,
    );
  }

  if (items.length === 0) {
    throw new Error(
      'series 정의 포스트를 한 건도 찾지 못했습니다 — 빈 목록을 캐시에 쓰지 않고 중단합니다',
    );
  }

  return items;
};

/**
 * 캐시된 블로그 리스트에서 시리즈 slug 별 로케일 멤버 수를 센다.
 * 시리즈마다 리스트를 다시 훑지 않도록 한 번의 순회로 집계한다.
 */
const countMembersBySeries = (allPosts: BlogNewsListItemProp[]) => {
  const countBySlug = new Map<string, { ko: number; en: number }>();

  for (const post of allPosts) {
    if (!post.series) continue;

    const entry = countBySlug.get(post.series) ?? { ko: 0, en: 0 };
    if (post.lang?.includes('ko')) entry.ko += 1;
    if (post.lang?.includes('en')) entry.en += 1;
    countBySlug.set(post.series, entry);
  }

  return countBySlug;
};

/**
 * 전체 시리즈의 카드 데이터를 집계한다 — 시리즈 목록 페이지와 블로그 랜딩이 이걸 하나만 읽는다.
 *
 * 두 소스로 자기완결한다. 정의 포스트는 전용 쿼리(`SERIES_LIST_QUERY`)로 slug·본문·발행일만
 * 받아 표시명·설명문·썸네일을 `parseSeriesSource` 로 뽑고, 멤버 수는 캐시된 블로그 리스트에서
 * `series` 로 집계한다. 시리즈별 캐시(`getSeries-{slug}`)를 읽어 모으지 않는다 — 캐시가 캐시를
 * 읽으면 생성 순서에 의존하게 된다.
 *
 * 제외 규칙 두 가지. 둘 다 조용히 넘기지 않고 로그를 남긴다 — 운영자가 "새로 만든 시리즈가
 * 목록에 없다"고 할 때 유일한 단서다:
 * - 예약 slug(`all`) — 목록 페이지 URL 이 선점했다
 * - **멤버 글이 한 건도 없는 시리즈**(ko·en 모두 0) — 정의글만 만들고 글을 안 붙인 상태다.
 *   한 쪽 로케일만 0건인 시리즈는 **남긴다** — 반대 로케일에선 정상이다(`year-in-review` =
 *   ko 0 / en 5). 로케일별 제외는 화면 필터의 몫이다.
 *
 * (S3 업로드 없음 — 검증/dry-run 용으로도 사용)
 */
export const buildSeriesList = async (
  options?: BuildOptions,
): Promise<SeriesListItemData[]> => {
  const [sourceData, allPosts] = await Promise.all([
    fetchFromWordpress(
      SERIES_LIST_QUERY,
      { variables: { postCount: SERIES_LIST_PAGE_SIZE } },
      options,
    ),
    // strict: 부분 읽기(중간 페이지 유실)를 에러로 받는다. 기본 동작은 조용히 잘린 목록을
    // 주는데, 그걸로 멤버를 세면 개수가 틀리고 뒤쪽 페이지에만 멤버가 있는 시리즈는 0건으로
    // 집계돼 목록에서 통째로 빠진다 — 그 상태가 캐시에 박제되면 두 화면이 함께 어긋난다
    getBlogNewsPostsForList(CONTENT_CATEGORY_ID.blog, { strict: true }),
  ]);

  const items = toSeriesDefinitionPosts(sourceData);

  // strict 는 페이지 유실만 잡는다 — 1페이지가 정상인데 내용이 0건인 경우는 여기서 막는다
  if (allPosts.length === 0) {
    throw new Error(
      '블로그 리스트 캐시가 비어 멤버를 셀 수 없습니다 — 빈 목록을 캐시에 쓰지 않고 중단합니다',
    );
  }

  const countBySlug = countMembersBySeries(allPosts);

  // 정의글 WP 발행일 내림차순. ACF `date` 필드는 쓰지 않는다 — 시리즈 정의글은 전건
  // 미입력이라(실측) 그 값으로는 정렬이 되지 않는다. 발행일은 초 단위까지 들어와
  // (`2026-07-29T14:57:07`) 동률이 사실상 없어 2차 키가 필요 없고, 값이 없으면 toEpoch 가
  // 0 을 주어 맨 뒤로 간다.
  const sorted = [...items].sort((a, b) => toEpoch(b.date) - toEpoch(a.date));

  const list: SeriesListItemData[] = [];

  for (const item of sorted) {
    const slug = item.slug;
    if (!slug) continue;

    if (slug === RESERVED_SERIES_SLUG) {
      console.warn(
        `📚 시리즈 목록에서 제외 — slug '${RESERVED_SERIES_SLUG}' 는 목록 페이지 URL 이 선점한 예약어입니다. CMS 에서 slug 를 변경하세요`,
      );
      continue;
    }

    const count = countBySlug.get(slug) ?? { ko: 0, en: 0 };
    if (count.ko + count.en === 0) {
      console.warn(
        `📚 시리즈 목록에서 제외 — '${slug}' 에 속한 글이 없습니다(빈 시리즈). 글을 저장하면 목록이 재생성되어 자동으로 들어옵니다`,
      );
      continue;
    }

    const source = parseSeriesSource(item.content);

    // 표시명이 비면 카드 제목이 빈 줄로 렌더된다. 폴백은 소비 측 정책이라 여기서는 알리기만 한다
    const missingLabels = (['ko', 'en'] as const).filter(
      locale => !source.label[locale],
    );
    if (missingLabels.length > 0) {
      console.warn(
        `📚 '${slug}' 의 ${missingLabels.join('·')}-label 이 CMS 에 없습니다 — 그 로케일 카드 제목이 빈 줄로 나갑니다`,
      );
    }

    list.push({
      slug,
      image: source.image,
      ko: {
        name: source.label.ko,
        description: source.description.ko,
        postCount: count.ko,
      },
      en: {
        name: source.label.en,
        description: source.description.en,
        postCount: count.en,
      },
    });
  }

  return list;
};

/**
 * WordPress 응답 노드 검증 — S3 캐시와 마찬가지로 신뢰 경계다.
 * 특히 본문 없이 발행된 정의글은 `content: null` 로 오는데, 그대로 `parseSeriesSource` 에
 * 넘기면 `toCmsSourceLines` 의 `content.replace` 에서 TypeError 로 죽어 목록 빌드 전체가
 * 실패한다(단건 조회 `fetchSeriesSource` 는 같은 이유로 null 을 명시적으로 걸러낸다).
 */
const isSeriesDefinitionPost = (
  value: unknown,
): value is SeriesDefinitionPost => {
  if (typeof value !== 'object' || value === null) return false;

  const node = value as Partial<SeriesDefinitionPost>;
  return (
    typeof node.slug === 'string' &&
    typeof node.content === 'string' &&
    typeof node.date === 'string'
  );
};

/** 캐시 원소 검증 — 스키마가 어긋난 캐시가 조용히 통과하지 않도록 전 필드 검사 */
const isSeriesListLocaleData = (
  value: unknown,
): value is SeriesListLocaleData => {
  if (typeof value !== 'object' || value === null) return false;

  const data = value as Partial<SeriesListLocaleData>;
  return (
    typeof data.name === 'string' &&
    typeof data.description === 'string' &&
    typeof data.postCount === 'number'
  );
};

const isSeriesListItemData = (value: unknown): value is SeriesListItemData => {
  if (typeof value !== 'object' || value === null) return false;

  const item = value as Partial<SeriesListItemData>;
  return (
    typeof item.slug === 'string' &&
    typeof item.image === 'string' &&
    isSeriesListLocaleData(item.ko) &&
    isSeriesListLocaleData(item.en)
  );
};

/**
 * 시리즈 목록 조회
 * - refresh: true → 집계 + S3 업로드 (스크립트/웹훅용, 실패 시 throw)
 * - refresh: false → S3 캐시 조회 (빌드/런타임용, 캐시가 없으면 빈 배열)
 *
 * ⚠️ refresh 는 `syncSeriesList`(content-cache-handlers)를 통해서만 호출한다 — 재생성과
 * 태그 무효화가 짝이라 여기서 직접 부르면 무효화를 빠뜨리게 된다(스크립트는 예외 —
 * Next 런타임이 없어 무효화 대상이 없다).
 */
export const getSeriesList = async (
  options?: Pick<FetchAPIOptions, 'refresh' | 'prodFilter'>,
): Promise<SeriesListItemData[]> => {
  if (options?.refresh) {
    const list = await buildSeriesList({ prodFilter: options.prodFilter });
    await uploadWordpressCacheDataToS3(list, SERIES_LIST_CACHE_LABEL);
    return list;
  }

  const data = await fetchFromCache(SERIES_LIST_CACHE_LABEL);
  return Array.isArray(data) ? data.filter(isSeriesListItemData) : [];
};

/**
 * 블로그 포스트(databaseId)의 현재 시리즈 slug 를 **캐시된 블로그 리스트**에서 읽는다.
 * (블로그 포스트 저장 → 소속 시리즈 재생성 커플링에서 "현재 값"으로 사용)
 *
 * 커플링은 updateSinglePostCache 로 블로그 리스트 캐시가 갱신된 직후에 돌므로,
 * 라이브 조회 없이 그 리스트에서 databaseId 로 찾으면 최신 series 를 얻는다.
 * 리스트에 없으면(삭제/미배포 등) null.
 */
export const getPostSeriesFromCache = async (
  databaseId: number,
): Promise<string | null> => {
  const allPosts = await getBlogNewsPostsForList(CONTENT_CATEGORY_ID.blog);
  return allPosts.find(post => post.databaseId === databaseId)?.series || null;
};

/**
 * 포스트별 "마지막으로 반영된 시리즈 slug" 캐시.
 *
 * 블로그 포스트의 시리즈가 A→B 로 바뀌면 이전(A)·이후(B) 시리즈를 모두 재생성해야
 * B 만 갱신하고 A 에 예전 멤버가 남는 버그가 없다. 웹훅은 stateless 라 이전 값을
 * S3 에 별도로 기억한다(공유 블로그 상세 쿼리에 series 를 넣으면 운영 스키마 갭으로
 * 블로그 캐시가 깨지므로, 상세 캐시를 오염시키지 않도록 전용 캐시로 분리).
 */
const getPostSeriesLabel = (databaseId: number) =>
  `getPostSeries-${databaseId}`;

/** 포스트의 마지막 반영 시리즈 slug 조회 (없으면 null) */
export const getLastPostSeries = async (
  databaseId: number,
): Promise<string | null> => {
  const data = await fetchFromCache(getPostSeriesLabel(databaseId));
  return data?.series ?? null;
};

/** 포스트의 마지막 반영 시리즈 slug 저장 */
export const setLastPostSeries = async (
  databaseId: number,
  series: string | null,
): Promise<void> => {
  await uploadWordpressCacheDataToS3(
    { series },
    getPostSeriesLabel(databaseId),
  );
};

/**
 * 블로그 포스트 저장 시 재생성해야 할 시리즈 slug 목록을 정한다.
 * - 현재 시리즈는 **변경이 없어도 항상** 재생성 → 멤버의 제목·이미지·날짜 등 내용 수정을 반영
 * - 시리즈가 바뀌었으면 이전 시리즈도 함께 재생성 → 이전 시리즈에서 이 글이 빠지도록
 * - 시리즈물이 아니면(이전·이후 모두 없음) → 빈 배열 (아무 조치 불필요)
 */
export const seriesToRefreshForPost = (
  oldSeries: string | null,
  newSeries: string | null,
): string[] => {
  const present = [oldSeries, newSeries].filter(Boolean) as string[];
  return Array.from(new Set(present));
};

// ============================================
// 조회 (빌드/런타임 + refresh)
// ============================================

/**
 * 시리즈 데이터 조회
 * - refresh: true → WordPress 조회 + 캐시 빌드 + S3 업로드 (스크립트/웹훅용, 실패 시 throw)
 * - refresh: false → S3 캐시 조회 (빌드/런타임용, 실패 시 null)
 */
export const getSeries = async (
  slug: string,
  options?: FetchAPIOptions,
): Promise<SeriesPageData | null> => {
  const refresh = options?.refresh ?? false;
  const label = getSeriesCacheLabel(slug);

  if (refresh) {
    const data = await buildSeriesData(slug, {
      prodFilter: options?.prodFilter,
    });
    await uploadWordpressCacheDataToS3(data, label);
    return data;
  }

  return fetchFromCache(label);
};
